/**
 * Componente Modal: Términos y Condiciones y Política de Privacidad
 */

'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck } from 'lucide-react';
import { Button } from '@/shared/components/ui';

interface TerminosCondicionesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
}

export function TerminosCondicionesModal({
  isOpen,
  onClose,
  onAccept,
}: TerminosCondicionesModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden border border-gray-100"
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
        >
          {/* Cabecera */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/70">
            <div className="flex items-center gap-2.5">
              <span className="p-2 rounded-lg bg-emerald-50 text-[#78A83B]">
                <ShieldCheck size={20} />
              </span>
              <div>
                <h3 className="font-bold text-gray-900 text-base">
                  Términos y Condiciones de Servicio
                </h3>
                <p className="text-xs text-gray-500">AviFácil Cloud Agrícola y Zootécnica</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Cerrar modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Cuerpo de texto con scroll */}
          <div className="p-6 overflow-y-auto space-y-4 text-sm text-gray-600 leading-relaxed">
            <section>
              <h4 className="font-bold text-gray-900 mb-1 text-sm">1. Objeto y Alcance de la Plataforma</h4>
              <p>
                AviFácil es una plataforma agronómica y zootécnica diseñada para optimizar la gestión
                productiva, trazabilidad de lotes, control de mortalidad y contabilidad bajo normas NIIF
                en explotaciones avícolas.
              </p>
            </section>

            <section>
              <h4 className="font-bold text-gray-900 mb-1 text-sm">2. Propiedad y Confidencialidad de la Información</h4>
              <p>
                Toda la información registrada referente a censos, curvas de producción, pesos corporales
                y registros contables es de propiedad exclusiva del titular del predio avícola. AviFácil
                garantiza el aislamiento lógico multi-inquilino mediante Row Level Security (RLS) y no comercializa
                datos de producción con terceros.
              </p>
            </section>

            <section>
              <h4 className="font-bold text-gray-900 mb-1 text-sm">3. Responsabilidad sobre Credenciales de Acceso</h4>
              <p>
                El usuario titular es el responsable único de custodiar sus credenciales de acceso y de asignar
                los permisos correspondientes a los operarios o médicos veterinarios de su predio.
              </p>
            </section>

            <section>
              <h4 className="font-bold text-gray-900 mb-1 text-sm">4. Tratamiento de Datos Personales (Habeas Data)</h4>
              <p>
                En cumplimiento de las normativas vigentes de protección de datos, la recolección de correos
                electrónicos y números de teléfono móvil tiene como finalidad exclusiva la autenticación,
                notificaciones de alertas críticas zootécnicas y soporte técnico personalizado.
              </p>
            </section>
          </div>

          {/* Pie de modal */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50">
            <Button variant="ghost" onClick={onClose}>
              Entendido
            </Button>
            {onAccept && (
              <Button
                variant="freshTech"
                onClick={() => {
                  onAccept();
                  onClose();
                }}
              >
                Aceptar Términos
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
